import { z } from 'zod';

/**
 * A drawing's SCENE as it goes over the wire — shared by the design board's card sketches and the
 * measurement sketches, which save the same canvas's output and so must refuse the same things.
 *
 * The scene is a STRING and not an object on purpose. The server never reads inside it — it is
 * the drawing tool's format, versioned by the drawing tool — and a schema that claimed to know
 * its shape would have to be revised every time that tool adds an element type. What the server
 * does enforce is a size ceiling and that it parses as JSON, which is the whole of what it can
 * honestly check.
 */

/**
 * Biggest scene we will store, well under Mongo's 16MB document ceiling.
 *
 * A drawing is a few kilobytes per element, so this is thousands of strokes — far past the point
 * where a neckline sketch has become something else. Measured on the JSON string's UTF-8 bytes.
 */
export const SKETCH_SCENE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * `JSON.parse` is the only inspection: it rejects a truncated upload, which is the failure that
 * actually happens on a market-street connection, and it refuses to let the field become a
 * dumping ground for arbitrary text. What is INSIDE the JSON is the canvas's business.
 */
export const sketchSceneSchema = z
  .string()
  .min(2, 'The drawing is empty')
  .refine((scene) => new TextEncoder().encode(scene).length <= SKETCH_SCENE_MAX_BYTES, {
    message: 'That drawing is too large to save',
  })
  .refine(
    (scene) => {
      try {
        JSON.parse(scene);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'The drawing could not be read' },
  );
